import { useSyncExternalStore } from 'react';

import { API_BASE_URL } from '@/lib/env';
import { log } from './log';

/**
 * Whether the counter can actually reach the server.
 *
 * `navigator.onLine` is not that question. It answers "is there a network
 * interface up", which on a shop's wifi is true while the broadband is down,
 * true on a captive portal, and true when the backend itself is unreachable.
 * A till that believes it is online when it is not will hang on every save.
 *
 * So the real signal is a cheap probe against the API, run on a timer and
 * whenever the browser thinks something changed. `navigator.onLine` is still
 * used, but only as a shortcut in the one direction it is reliable: if it says
 * offline, it is offline, and there is no point probing.
 */

const PROBE_PATH = '/healthz';
const ONLINE_EVERY = 30_000;
const OFFLINE_EVERY = 5_000;
/** A probe that has not answered by now is a dead line, not a slow one. */
const PROBE_TIMEOUT = 4_000;

let online = navigator.onLine;
let checking = false;
let timer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function set(next: boolean) {
  if (next === online) return;
  online = next;
  log.net(next ? 'line is UP' : 'line is DOWN');
  emit();
}

async function probe(): Promise<void> {
  if (checking) return;
  if (!navigator.onLine) {
    set(false);
    return;
  }

  checking = true;
  const controller = new AbortController();
  const bail = setTimeout(() => controller.abort(), PROBE_TIMEOUT);

  try {
    /*
     * The health probe rather than an API route: it is unauthenticated, so an
     * expired token cannot be mistaken for a dead network, and `cache: 'no-store'`
     * keeps a service worker or the browser from answering from memory and
     * reporting a server that is not there.
     */
    const base = API_BASE_URL.replace(/\/api\/v1\/?$/, '');
    const started = performance.now();
    const response = await fetch(`${base}${PROBE_PATH}`, {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal,
    });
    log.net(`probe ${response.status} in ${Math.round(performance.now() - started)}ms`);
    set(response.ok);
  } catch {
    log.net('probe failed');
    set(false);
  } finally {
    clearTimeout(bail);
    checking = false;
    schedule();
  }
}

function schedule() {
  if (timer) clearTimeout(timer);
  // Polls harder while down, because the moment it comes back is when the
  // queue should start draining.
  timer = setTimeout(() => void probe(), online ? ONLINE_EVERY : OFFLINE_EVERY);
}

let started = false;

export function startConnectivityWatch(): void {
  if (started) return;
  started = true;

  window.addEventListener('online', () => void probe());
  window.addEventListener('offline', () => set(false));
  // A laptop coming out of sleep has a stale answer either way.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void probe();
  });

  void probe();
}

/**
 * Reports a request that failed at the network level.
 *
 * The probe runs on a timer, so between the line dying and the next probe there
 * is a window in which the app still believes it is connected. A real request
 * failing is better evidence than any probe, so whatever discovers it says so
 * immediately and the whole UI follows within the same tick.
 */
export function reportUnreachable(): void {
  set(false);
  schedule();
}

/**
 * Reports a request that succeeded.
 *
 * Recovery should feel as immediate as the drop. Waiting for the poll to come
 * round would leave the pill claiming "working offline" for up to five seconds
 * after the line was demonstrably back.
 */
export function reportReachable(): void {
  set(true);
  schedule();
}

/** Forces a check — used the moment something needs to know before acting. */
export function recheckNow(): Promise<void> {
  return probe();
}

export function isOnline(): boolean {
  return online;
}

export function useOnline(): boolean {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => online,
    () => true,
  );
}
